// src/features/excursions/ExcursionsTab.jsx
import React, { useState, useMemo } from "react";
import { useExcursionsData } from "./hooks/useExcursionsData";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import ExcursionTable from "./components/ExcursionTable";
import { formatDateToYYYYMMDD } from "utils/dateUtils";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import AddExcursionModal from "./components/AddExcursionModal";
import EditExcursionModal from "./components/EditExcursionModal";
import { Button } from "components/ui/button";
import { CalendarCheck, FileText, LayoutGrid, List } from "lucide-react";
import { checkHoliday } from "services/holidayService";
import OrcamentoModal from "./components/OrcamentoModal";
import PendingExcursionsAlert from "./components/PendingExcursionsAlert";
import toast from "react-hot-toast";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value || 0,
  );

function ExcursionsTab({ userRole, searchTerm }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isOrcamentoModalOpen, setIsOrcamentoModalOpen] = useState(false);
  const [editingExcursion, setEditingExcursion] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [desktopViewMode, setDesktopViewMode] = useState("cards"); // 'cards' ou 'list'

  const {
    excursions,
    totalClients,
    dailyLimit,
    totalPayments,
    isLoading,
    error,
    markAsPresent,
    updateLunchInfo,
    addExcursion,
    updateExcursion,
    deleteExcursion,
  } = useExcursionsData(selectedDate, searchTerm);

  const holidayName = useMemo(() => {
    return checkHoliday(selectedDate);
  }, [selectedDate]);

  const sortedExcursions = useMemo(() => {
    return [...excursions].sort((a, b) => {
      if (sortDirection === "asc") {
        return a.clientName.localeCompare(b.clientName);
      } else {
        return b.clientName.localeCompare(a.clientName);
      }
    });
  }, [excursions, sortDirection]);

  const handleDateChange = (event) => {
    const [year, month, day] = event.target.value.split("-").map(Number);
    setSelectedDate(new Date(year, month - 1, day));
  };

  const handleSort = () => {
    setSortDirection((currentDirection) =>
      currentDirection === "asc" ? "desc" : "asc",
    );
  };

  const handleAddExcursionClick = () => {
    if (
      userRole !== "admin" &&
      dailyLimit !== null &&
      totalClients >= dailyLimit
    ) {
      toast.error(
        `Atenção: O limite diário de ${dailyLimit} clientes já foi atingido para esta data. Contate o Administrador.`,
        { icon: "🛑" },
      );
      return;
    }
    setIsAddModalOpen(true);
  };

  const canEdit = userRole === "admin" || userRole === "editor";
  const isSearchMode = searchTerm && searchTerm.length > 3;

  return (
    <div className="space-y-6">
      <PendingExcursionsAlert 
        userRole={userRole} 
        onSelectDate={(date) => setSelectedDate(date)} 
      />

      {/* --- VISUALIZAÇÃO DESKTOP --- */}
      <div className="hidden md:flex flex-wrap justify-between items-end gap-4">
        <div className="flex items-end gap-4">
          <div className="w-auto">
            {isSearchMode ? (
              <div className="p-2 bg-blue-100 text-blue-800 rounded border border-blue-200">
                <strong>Modo de Busca:</strong> Exibindo resultados para '
                {searchTerm}'
              </div>
            ) : (
              <>
                <Label
                  htmlFor="excursion-date-desktop"
                  className="font-semibold"
                >
                  Selecione a Data
                </Label>

                <div className="flex flex-col w-fit">
                  <Input
                    type="date"
                    id="excursion-date-desktop"
                    value={formatDateToYYYYMMDD(selectedDate)}
                    onChange={handleDateChange}
                    className="mt-2 force-light-datepicker w-full min-w-[160px]"
                  />
                  {holidayName && (
                    <div className="mt-1 flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-bold px-2 py-1 rounded border border-purple-200 whitespace-nowrap w-full">
                      <CalendarCheck className="h-3 w-3 flex-shrink-0" />
                      {holidayName}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {!isLoading && !error && !isSearchMode && (
            <>
              <div className="border-l pl-4">
                <p className="text-sm text-muted-foreground">
                  Total de Clientes
                </p>
                <p className="text-2xl font-bold">{totalClients}</p>
              </div>
              <div className="border-l pl-4">
                <p className="text-sm text-muted-foreground">Total Pago</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(totalPayments)}
                </p>
              </div>
            </>
          )}
        </div>
        <div className="flex gap-2">
          {!isSearchMode && (
            <div className="flex bg-muted/50 p-1 rounded-md border border-border">
              <Button
                variant={desktopViewMode === "cards" ? "secondary" : "ghost"}
                size="icon"
                className={`h-8 w-8 ${desktopViewMode === "cards" ? "bg-background shadow-sm" : ""}`}
                onClick={() => setDesktopViewMode("cards")}
                title="Visualização em Cards"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={desktopViewMode === "list" ? "secondary" : "ghost"}
                size="icon"
                className={`h-8 w-8 ${desktopViewMode === "list" ? "bg-background shadow-sm" : ""}`}
                onClick={() => setDesktopViewMode("list")}
                title="Visualização em Lista"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          )}
          {canEdit && !isSearchMode && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsOrcamentoModalOpen(true)}
              >
                <FileText className="w-4 h-4 mr-2" /> Orçamento
              </Button>
              <Button onClick={handleAddExcursionClick}>
                Adicionar Excursão
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* --- VISUALIZAÇÃO MOBILE --- */}
      <div className="md:hidden flex flex-col gap-4">
        {/* Métricas (Lado a Lado) */}
        {!isLoading && !error && !isSearchMode && (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 dark:bg-card p-3 rounded-md border text-center shadow-sm">
              <div className="text-xs text-slate-500 mb-1">Total Clientes</div>
              <div
                className={`text-xl font-bold ${dailyLimit !== null && totalClients >= dailyLimit ? "text-red-600 dark:text-red-400" : "text-slate-800 dark:text-foreground"}`}
              >
                {totalClients}{" "}
                {dailyLimit !== null && (
                  <span className="text-xs font-medium text-slate-500">
                    / {dailyLimit}
                  </span>
                )}
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-card p-3 rounded-md border text-center shadow-sm">
              <div className="text-xs text-slate-500 mb-1">Total Pago</div>
              <div className="text-xl font-bold text-slate-800 dark:text-foreground">
                {formatCurrency(totalPayments)}
              </div>
            </div>
          </div>
        )}

        {/* Busca e Data */}
        <div className="w-full">
          {isSearchMode ? (
            <div className="p-2 bg-blue-100 text-blue-800 rounded border border-blue-200 text-xs text-center">
              Busca: '{searchTerm}'
            </div>
          ) : (
            <div className="flex flex-col w-fit">
              <Label
                htmlFor="excursion-date-mobile"
                className="font-semibold text-sm mb-1"
              >
                Selecione a Data
              </Label>
              <Input
                type="date"
                id="excursion-date-mobile"
                value={formatDateToYYYYMMDD(selectedDate)}
                onChange={handleDateChange}
                className="force-light-datepicker w-full"
              />
              {holidayName && (
                <div className="mt-2 flex items-center justify-center gap-1 bg-purple-100 text-purple-800 text-xs font-bold px-2 py-2 rounded border border-purple-200 w-full text-center">
                  <CalendarCheck className="h-4 w-4 shrink-0" />
                  <span className="truncate">{holidayName}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Botões */}
        {canEdit && !isSearchMode && (
          <div className="grid grid-cols-2 gap-2 mt-1">
            <Button
              variant="outline"
              className="w-full h-11"
              onClick={() => setIsOrcamentoModalOpen(true)}
            >
              <FileText className="w-4 h-4 mr-1" /> Orçamento
            </Button>
            <Button className="w-full h-11" onClick={handleAddExcursionClick}>
              Adicionar
            </Button>
          </div>
        )}
      </div>

      {isLoading && <LoadingSpinner />}
      {error && (
        <p className="text-red-500 bg-red-50 p-4 rounded-md">Erro: {error}</p>
      )}

      {!isLoading && !error && (
        <ExcursionTable
          excursions={sortedExcursions}
          onMarkPresent={markAsPresent}
          onUpdateLunchInfo={updateLunchInfo}
          onUpdateExcursion={updateExcursion}
          canEdit={canEdit}
          onEditExcursion={setEditingExcursion}
          onDeleteExcursion={deleteExcursion}
          sortDirection={sortDirection}
          onSort={handleSort}
          desktopViewMode={desktopViewMode}
        />
      )}

      <AddExcursionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddExcursion={addExcursion}
        defaultDate={selectedDate}
      />

      {editingExcursion && (
        <EditExcursionModal
          isOpen={!!editingExcursion}
          onClose={() => setEditingExcursion(null)}
          onUpdateExcursion={updateExcursion}
          excursionData={editingExcursion}
        />
      )}

      <OrcamentoModal
        isOpen={isOrcamentoModalOpen}
        onClose={() => setIsOrcamentoModalOpen(false)}
      />
    </div>
  );
}

export default ExcursionsTab;
